import { useCallback, useEffect } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useKnowledgeSourcesStore, useKnowledgeSourcesActions } from './index';

/**
 * Ensures the connectors store is populated. Calls the knowledge configuration
 * API if the store is empty, otherwise no-ops. Pass `enabled: false` to skip the
 * fetch entirely (e.g. a caller that only needs connectors for some cohorts and
 * must not pay the fetch on every render); defaults to `true`.
 */
export const useEnsureConnectorsLoaded: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam & { enabled?: boolean },
) => void = createHookWithAssistanceService(
	({
		enabled = true,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
	}: AssistanceServiceInjectedParams & { enabled?: boolean }) => {
		const [{ hasFetchAttempted, isFetching }] = useKnowledgeSourcesStore();
		const { fetchIfEmpty } = useKnowledgeSourcesActions();

		const fetcher = useCallback(async () => {
			experienceTracker.start({
				id: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
				name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
			});
			try {
				const response = await assistanceService.getAgentKnowledgeConfiguration();
				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
				});
				return response;
			} catch (error) {
				experienceTracker.fail({
					name: ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE,
					error: error instanceof Error ? error : new Error(String(error)),
				});
				throw error;
			}
		}, [assistanceService, experienceTracker]);

		useEffect(() => {
			if (enabled && !hasFetchAttempted && !isFetching) {
				fetchIfEmpty(fetcher, siteId);
			}
		}, [enabled, hasFetchAttempted, isFetching, fetchIfEmpty, fetcher, siteId]);
	},
);
